// TEMP: internal-only KYB tester. Not meant for customers; remove once KYB moves into the upgrade flow.
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import Button from "../design/Button";
import {
  CCDropdown,
  CustomInput,
} from "../design/components/customComponents";
import styles from "./Kyb.module.css";

const KYB_BASE_URL =
  "https://sandbox-dev3.frejun.com/api/v1/integrations/kyb/temp";
const KYB_AUTH_KEY = "fj-4k9Xp2Lm7Qr8Wv1Yt3Zx5Jc6Vb9Nf2Gh";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

const kybClient = axios.create({
  baseURL: KYB_BASE_URL,
  headers: { Authorization: KYB_AUTH_KEY },
});

const initialAddress = {
  line1: "",
  line2: "",
  city: "",
  pincode: "",
  state: "",
  country: "IN",
};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function Kyb() {
  const reduxAccount = useSelector((state) => state?.account?.value);
  const orgId = reduxAccount?.selectedOrg || reduxAccount?.orgId;

  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState(initialAddress);
  const [businessId, setBusinessId] = useState("");
  const [taxId, setTaxId] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [isInitiating, setIsInitiating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [kybResult, setKybResult] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // prefill company name and contact number from the logged in account (once)
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if (hasPrefilled.current || !reduxAccount) return;
    hasPrefilled.current = true;

    const org = reduxAccount?.allOrgs?.find((elem) => elem?.id === orgId);
    if (org?.company) setCompanyName(org.company);

    const number = reduxAccount?.data?.number;
    if (number?.phone_number) {
      const cc = number?.country_code || "";
      if (cc) setCountryCode(cc);
      setPhoneNumber(
        cc && number.phone_number.startsWith(cc)
          ? number.phone_number.slice(cc.length)
          : number.phone_number,
      );
    }
  }, [reduxAccount, orgId]);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const requiredFilled =
    companyName.trim() &&
    address.line1.trim() &&
    address.city.trim() &&
    address.pincode.trim() &&
    address.state.trim() &&
    address.country.trim() &&
    businessId.trim();

  const fetchResult = async (id) => {
    const res = await kybClient.get("/result", { params: { session_id: id } });
    if (!res?.data?.success)
      throw new Error(res?.data?.message || "Failed to fetch KYB result");
    return res.data.data;
  };

  const isFinal = (data) =>
    data?.status === "COMPLETED" || !!data?.result || !!data?.failure_message;

  const pollResult = async (id) => {
    setIsPolling(true);
    try {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (!isMounted.current) return;
        const data = await fetchResult(id);
        if (!isMounted.current) return;
        setKybResult(data);
        if (isFinal(data)) return;
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      enqueueSnackbar(
        "KYB is still processing. Use 'Refresh result' to check again.",
        { variant: "info" },
      );
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, "Failed to fetch KYB result"), {
        variant: "error",
      });
    } finally {
      if (isMounted.current) setIsPolling(false);
    }
  };

  const handleInitiate = async () => {
    const payload = {
      business_name: companyName.trim(),
      business_id: businessId.trim(),
      address: [address.line1, address.line2]
        .map((line) => line.trim())
        .filter(Boolean)
        .join(", "),
      state: address.state.trim(),
      city: address.city.trim(),
      pincode: address.pincode.trim(),
      country: address.country.trim().toUpperCase(),
    };

    setIsInitiating(true);
    setSessionId("");
    setKybResult(null);
    try {
      const res = await kybClient.post("/initiate", payload);
      if (!res?.data?.success)
        throw new Error(res?.data?.message || "Failed to initiate KYB");

      const id = res?.data?.data?.session_id ?? res?.data?.session_id;
      if (!id) throw new Error("KYB initiated but no session ID was returned");

      setSessionId(id);
      enqueueSnackbar(res?.data?.message || "KYB initiated", {
        variant: "success",
      });
      setIsInitiating(false);
      await pollResult(id);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, "Failed to initiate KYB"), {
        variant: "error",
      });
    } finally {
      if (isMounted.current) setIsInitiating(false);
    }
  };

  const resultTone = (() => {
    const result = kybResult?.result?.toUpperCase?.();
    if (!result) return styles.badgeNeutral;
    if (["FAILED", "REJECTED", "ERROR"].includes(result))
      return styles.badgeError;
    return styles.badgeSuccess;
  })();

  return (
    <div className="screen-container" style={{ paddingTop: "32px" }}>
      <p className="t3 medium-font nc-gray-900">KYB</p>

      <div className={styles.container}>
        <p className={`t5 medium-font nc-gray-900 ${styles.heading}`}>
          Organization details
        </p>

        <label className={styles.field}>
          <span className="t7 medium-font">Company name</span>
          <input
            className={styles.input}
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </label>

        <div className={styles.field}>
          <span className="t7 medium-font">Address</span>
          <input
            className={styles.input}
            type="text"
            placeholder="Address line 1"
            name="line1"
            value={address.line1}
            onChange={handleAddressChange}
          />
          <input
            className={styles.input}
            type="text"
            placeholder="Address line 2"
            name="line2"
            value={address.line2}
            onChange={handleAddressChange}
          />
          <div className={styles.row}>
            <input
              className={styles.input}
              type="text"
              placeholder="City"
              name="city"
              value={address.city}
              onChange={handleAddressChange}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="Pin code"
              name="pincode"
              value={address.pincode}
              onChange={handleAddressChange}
            />
          </div>
          <div className={styles.row}>
            <input
              className={styles.input}
              type="text"
              placeholder="State"
              name="state"
              value={address.state}
              onChange={handleAddressChange}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="Country (e.g. IN)"
              name="country"
              maxLength={2}
              value={address.country}
              onChange={handleAddressChange}
            />
          </div>
        </div>

        <label className={styles.field}>
          <span className="t7 medium-font">Business ID number</span>
          <input
            className={styles.input}
            type="text"
            value={businessId}
            onChange={(e) => setBusinessId(e.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className="t7 medium-font">
            Tax ID number <em className={styles.optional}>(Optional)</em>
          </span>
          <input
            className={styles.input}
            type="text"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
          />
        </label>

        <CustomInput
          label="Contact number"
          style={{ width: "100%" }}
          dropdown={
            <CCDropdown
              CCDropdownstyle={{ marginTop: "0px", border: "none" }}
              selectedCountryCode={countryCode}
              handleCountrySelect={(val) => setCountryCode(val)}
            />
          }
          value={{ value: phoneNumber }}
          handleChange={(e) => setPhoneNumber(e.target.value)}
        />

        <Button
          variant="contained"
          hierarchy="green"
          buttonText="Initiate KYB"
          onClick={handleInitiate}
          isLoading={isInitiating || isPolling}
          disabled={!requiredFilled || isInitiating || isPolling}
          styleOverride={{ textTransform: "none" }}
        />

        {sessionId && (
          <div className={styles.resultCard}>
            <div className={styles.resultHeader}>
              <p className="t6 medium-font nc-gray-900">KYB result</p>
              <Button
                variant="outlined"
                hierarchy="white"
                buttonText="Refresh result"
                onClick={() => pollResult(sessionId)}
                isLoading={isPolling}
                disabled={isPolling}
                styleOverride={{ textTransform: "none", height: "32px" }}
              />
            </div>
            <dl className={styles.resultList}>
              <dt>Session ID</dt>
              <dd>{sessionId}</dd>
              <dt>Status</dt>
              <dd>{kybResult?.status || (isPolling ? "Fetching..." : "-")}</dd>
              <dt>Result</dt>
              <dd>
                {kybResult?.result ? (
                  <span className={`${styles.badge} ${resultTone}`}>
                    {kybResult.result}
                  </span>
                ) : (
                  "-"
                )}
              </dd>
              {kybResult?.failure_message && (
                <>
                  <dt>Failure message</dt>
                  <dd>{kybResult.failure_message}</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
